import type { User } from 'oidc-client-ts';
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';
import type { Except } from 'type-fest';
import type { Result } from '../../../../react-commons/types/result';
import { ApplicationAuthenticationProviderType } from '../../resource/enumerations/applicationAuthenticationProviderType';
import { useProCeedApplicationInformation } from '../../resource/services/useProCeedApplicationInformation';
import type { UnauthenticatedAppJWTProps } from '../application/UnauthenticatedAppJWT';
import { SetJwtContext } from './SetJwtContext';
import { SetOpenIdContext } from './SetOpenIdContext';

export type Auth<
    TIsOpenId extends boolean,
    TAuthState extends 'loading' | 'authenticated' | 'unauthenticated' =
        | 'loading'
        | 'authenticated'
        | 'unauthenticated',
> = { isOpenId: TIsOpenId; authState: TAuthState } & (
    | { authState: 'loading' }
    | { authState: 'authenticated'; logout: () => void /* TODO Result<void> + userId, username */ }
    | ({ authState: 'unauthenticated' } & (
          | {
                isOpenId: true;
                login: () => void;
                silentLogin: (onSuccess?: (user: User) => void) => void;
            }
          | {
                isOpenId: false;
                login: (credentials: { username: string; password: string }) => Promise<Result<void>>;
            }
      ))
);

const AuthContext = createContext<Except<Auth<boolean, 'authenticated'>, 'authState' | 'isOpenId'> | undefined>(
    undefined
);

const AuthContextProvider = ({ children, auth }: { children: ReactNode; auth: Auth<boolean, 'authenticated'> }) => {
    const value = useMemo(
        () => ({
            logout: auth.logout,
        }),
        [auth.logout]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider!');
    }
    return context;
};

export const AuthProvider = ({
    children,
    loading,
    UnauthenticatedAppOIDC,
    UnauthenticatedAppJWT,
}: {
    children: ReactNode;
    loading: ReactNode;
    UnauthenticatedAppOIDC: (props: {
        login: () => void;
        silentLogin: (onSuccess?: (user: User) => void) => void;
    }) => ReactNode;
    UnauthenticatedAppJWT: (props: UnauthenticatedAppJWTProps) => ReactNode;
}) => {
    const [auth, setAuth] = useState<Auth<boolean> | undefined>(undefined);

    const appInfoQuery = useProCeedApplicationInformation();

    if (!appInfoQuery.data) {
        return loading;
    }

    return (
        <>
            {!auth || auth.authState === 'loading' ?
                loading
            : auth.authState === 'authenticated' ?
                <AuthContextProvider auth={auth}>{children}</AuthContextProvider>
            : auth.isOpenId ?
                <UnauthenticatedAppOIDC login={auth.login} silentLogin={auth.silentLogin} />
            :   <UnauthenticatedAppJWT login={auth.login} />}
            {appInfoQuery.data.authenticationProvider === ApplicationAuthenticationProviderType.OIDC ?
                <SetOpenIdContext
                    authority={appInfoQuery.data.oidcAuthenticationProviderIssuerURI}
                    client_id={appInfoQuery.data.oidcAuthenticationProviderFrontendClientId}
                    setAuth={setAuth}
                />
            :   appInfoQuery.data.authenticationProvider === ApplicationAuthenticationProviderType.EMBEDDED && (
                    <SetJwtContext setAuth={setAuth} />
                )
            }
        </>
    );
};
